// Imported from Racketly's existing Notion legal page.
// Legal operator updated to Waraira Tek LLC on October 26, 2026.
// Contact email updated to support@warairatek.com on October 26, 2026.
// Source: https://www.notion.so/11596e38c99e8050a8c3da9513981ae8
// Source last edited: 2025-08-02T15:23:31.872Z
// Source document date: 02/08/2025 (August 2, 2025).
export const legalDocuments = {
	privacy: {
		title: "Privacy Policy",
		path: "/privacy",
		updatedAt: "2026-10-26",
		introduction: (
			<>
				<p><strong>Racketly</strong> ("we," "us," or "our") respects the privacy of our users ("user" or "you"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application (the "Application").</p>
				<p>By using <strong>Racketly</strong>, you agree to this Privacy Policy and our Terms &amp; Conditions. IF YOU DO NOT AGREE, PLEASE DO NOT ACCESS OR USE THE APPLICATION.</p>
				<p>We reserve the right to update this Privacy Policy at any time. We will notify users of material changes by updating the "Last Updated" date and, where appropriate. Continued use of the Application after modifications constitutes acceptance of the revised policy.</p>
			</>
		),
		sections: [
			{
				id: "collection-of-your-information",
				title: "Collection of Your Information",
				content: (
					<>
						<p>We collect and store the following types of user data:</p>
						<ul>
							<li><strong>Personal Information</strong>: Name and email address (collected during registration or voluntarily provided by you).</li>
							<li><strong>Usage Data</strong>: Statistics related to your app usage, such as game plays, performance metrics, and analysis history.</li>
							<li><strong>Device Information</strong>: Device type, operating system version, and unique identifiers.</li>
							<li><strong>Health and Motion Data</strong>: Data from your device’s CoreMotion and WorkoutData frameworks, such as heart rate, movement patterns, velocity, orientation, and acceleration. This information is used exclusively for match performance analysis.</li>
						</ul>
						<p>Some of this information, specifically <strong>motion and health data</strong>, may be <strong>shared with trusted third-party providers</strong>, such as <strong>Google’s Gemini AI</strong>, for the purpose of generating automated performance insights. We only share this data when necessary for providing our core service functionality and analysis features.</p>
						<p>All information is securely stored and transmitted using industry-standard encryption. We do <strong>not sell your data</strong> to third parties for advertising or marketing purposes</p>
					</>
				),
			},
			{
				id: "how-we-use-your-information",
				title: "How We Use Your Information",
				content: (
					<>
						<p>We use your information to:</p>
						<ul>
							<li>Provide, maintain, and improve our services.</li>
							<li>Generate automated performance analysis via third-party AI platforms.</li>
							<li>Enable data synchronization across devices.</li>
							<li>Communicate important updates or security alerts.</li>
							<li>Comply with legal obligations and enforce our Terms &amp; Conditions.</li>
						</ul>
						<p>We do <strong>not</strong> sell, trade, or rent your personal data to third parties.</p>
					</>
				),
			},
			{
				id: "use-of-ai-and-data-sharing",
				title: "Use of AI and Data Sharing",
				content: (
					<>
						<p>We use <strong>Google’s Gemini AI</strong> to process motion and performance data. By using the Application, you acknowledge and agree that your motion and workout-related data—including CoreMotion sensor data and heart rate—may be shared with and processed by Google Gemini for analysis purposes.</p>
						<p>You also agree to Google's <strong>Terms of Service</strong> and acknowledge that this AI-powered analysis is <strong>not guaranteed to be accurate or reliable</strong>. It is intended for informational and training purposes only and should not be used as a substitute for professional medical, coaching, or fitness advice.</p>
						<p>We are <strong>not responsible for the accuracy</strong> of AI-generated insights or any decisions made based on them.</p>
					</>
				),
			},
			{
				id: "data-security",
				title: "Data Security",
				content: (
					<>
						<p>We take reasonable security measures to protect your data, including encryption and access restrictions. However, no system is completely secure, and we cannot guarantee absolute security. Users are responsible for maintaining the confidentiality of their account credentials.</p>
					</>
				),
			},
			{
				id: "your-rights",
				title: "Your Rights",
				content: (
					<>
						<p>If you are a resident of the <strong>European Economic Area (EEA)</strong>, you have the following rights under the <strong>General Data Protection Regulation (GDPR)</strong>:</p>
						<ul>
							<li><strong>Right to Access</strong>: Request a copy of your data.</li>
							<li><strong>Right to Rectification</strong>: Request correction of inaccurate or incomplete data.</li>
							<li><strong>Right to Erasure</strong>: Request deletion of your personal data.</li>
							<li><strong>Right to Restrict Processing</strong>: Request limitation on how your data is processed.</li>
							<li><strong>Right to Data Portability</strong>: Request a copy of your data in a commonly used format.</li>
							<li><strong>Right to Object</strong>: Object to the processing of your data for certain purposes.</li>
						</ul>
						<p>To exercise any of these rights, please contact us at: <strong><a href="mailto:support@warairatek.com">support@warairatek.com</a></strong>.</p>
					</>
				),
			},
			{
				id: "data-retention",
				title: "Data Retention",
				content: (
					<>
						<p>We retain your data for as long as your account remains active. If you delete your account, your data will be erased unless required for legal compliance.</p>
					</>
				),
			},
			{
				id: "third-party-services",
				title: "Third-Party Services",
				content: (
					<>
						<p>We may share your data with trusted third-party services as required to provide core functionality.</p>
						<p>In particular, we use <strong>Google’s Gemini AI platform</strong> to analyze motion and workout data you provide through the app. This includes, but is not limited to:</p>
						<ul>
							<li><strong>Motion sensor data</strong> (CoreMotion)</li>
							<li><strong>Heart rate and fitness metrics</strong> (WorkoutData)</li>
							<li><strong>Performance-related statistics</strong> derived from your usage</li>
						</ul>
						<p>This data is shared <strong>solely for the purpose of generating automated match analysis</strong> and is <strong>not used for advertising</strong> or any other commercial profiling. By using our motion analysis features, you explicitly agree to the <strong>Google Terms of Service</strong> and consent to this data processing.</p>
						<p>We do not sell your personal data and take all reasonable steps to ensure it is handled securely.</p>
						<p>We reserve the right to modify or update this Privacy Policy at any time. We will notify you of any material changes by posting the updated Privacy Policy within the App or by other means of communication. Your continued use of the App after the effective date of the revised Privacy Policy constitutes your acceptance of the changes.</p>
					</>
				),
			},
			{
				id: "contact",
				title: "Contact",
				content: (
					<>
						<p>If you have questions about this Privacy Policy, contact us at: <strong><a href="mailto:support@warairatek.com">support@warairatek.com</a></strong></p>
					</>
				),
			},
		],
	},
	terms: {
		title: "Terms and Conditions",
		path: "/terms",
		updatedAt: "2026-10-26",
		introduction: (
			<>
				<p>Please read these Terms and Conditions ("Terms", "Terms of Service") carefully before using <strong>Racketly</strong>, mobile application (the "Service", the "Site") operated by Waraira Tek LLC ("us", "we", or "our").</p>
				<p>Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users and others who access or use the Service.</p>
				<p><strong>By accessing or using the Service you agree to be bound by these Terms. If you disagree with any part of the terms then you may not access the Service.</strong></p>
				<p>The app is made available to you on an “as in”, “with all faults and “as available” basis. We cannot guarantee that the app will function or maintain the same level of service in the future. That is applicable for both free and paid users.</p>
				<p>By downloading and using <strong>Racketly</strong>, your use of the app is at your own risk. we make no endorsement, representation, or warranty of any kind about any content, information, or services.</p>
				<p>We reserve the right to modify or update this Privacy Policy at any time. We will notify you of any material changes by posting the updated Privacy Policy within the App or by other means of communication. Your continued use of the App after the effective date of the revised Privacy Policy constitutes your acceptance of the changes.</p>
			</>
		),
		sections: [
			{
				id: "use-of-the-service",
				title: "Use of the Service",
				content: (
					<>
						<ul>
							<li>You must be at least <strong>13 years old</strong> to use this Service.</li>
							<li>You are responsible for maintaining the security of your account.</li>
							<li>You may not use the Application for any illegal or unauthorized purposes.</li>
						</ul>
					</>
				),
			},
			{
				id: "disclaimer",
				title: "Disclaimer",
				content: (
					<>
						<p>THE SERVICE IS PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE SITE AND OUR SERVICES WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SITE AND YOUR USE THEREOF, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO WARRANTIES OR REPRESENTATIONS ABOUT THE ACCURACY OR COMPLETENESS OF THE SITE’S CONTENT OR THE CONTENT OF ANY WEBSITES LINKED TO THE SITE AND WE WILL ASSUME NO LIABILITY OR RESPONSIBILITY FOR ANY (1) ERRORS, MISTAKES, OR INACCURACIES OF CONTENT AND MATERIALS, (2) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER, RESULTING FROM YOUR ACCESS TO AND USE OF THE SITE, (3) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS AND/OR ANY AND ALL PERSONAL INFORMATION AND/OR FINANCIAL INFORMATION STORED THEREIN, (4) ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE SITE, (5) ANY BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE WHICH MAY BE TRANSMITTED TO OR THROUGH THE SITE BY ANY THIRD PARTY, AND/OR (6) ANY ERRORS OR OMISSIONS IN ANY CONTENT AND MATERIALS OR FOR ANY LOSS OR DAMAGE OF ANY KIND INCURRED AS A RESULT OF THE USE OF ANY CONTENT POSTED, TRANSMITTED, OR OTHERWISE MADE AVAILABLE VIA THE SITE. WE DO NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR ANY PRODUCT OR SERVICE ADVERTISED OR OFFERED BY A THIRD PARTY THROUGH THE SITE, ANY HYPERLINKED WEBSITE, OR ANY WEBSITE OR MOBILE APPLICATION FEATURED IN ANY BANNER OR OTHER ADVERTISING, AND WE WILL NOT BE A PARTY TO OR IN ANY WAY BE RESPONSIBLE FOR MONITORING ANY TRANSACTION BETWEEN YOU AND ANY THIRD-PARTY PROVIDERS OF PRODUCTS OR SERVICES.</p>
						<p>AS WITH THE PURCHASE OF A PRODUCT OR SERVICE THROUGH ANY MEDIUM OR IN ANY ENVIRONMENT, YOU SHOULD USE YOUR BEST JUDGMENT AND EXERCISE CAUTION WHERE APPROPRIATE.</p>
						<p>THE SERVICE CANNOT AND DOES NOT CONTAIN ANY MEDICAL, FITNESS OR HEALTH ADVICE. THE MEDICAL, FITNESS OR HEALTH INFORMATION IS PROVIDED FOR GENERAL INFORMATIONAL AND EDUCATIONAL PURPOSES ONLY AND IS NOT A SUBSTITUE FOR PROFESSIONAL ADVICE.</p>
						<p>ACCORDINGLY, BEFORE TAKING ANY ACTIONS BASED UPON SUCH INFORMATION, WE ENCOURAGE YOU TO CONSULT WITH THE APPROPIATE PROFESSIONALS. WE DO NOT PROVIDE ANY KIND OF MEDICAL, FITNESS OR HEALTH ADVICE. THE USE OR RELIANCE OF ANY INFORMATION CONTAINED ON THIS SERVICE IS SOLELY AT YOUR OWN RISK. THE OWNERS OF THE SITE NOR ANY OF AFFILIATES OR TRAINERS WILL BE LIABLE FOR ANY PHYSICAL OR MENTAL INJURY OR ILLNESS THAT MAY RESULT, WHETHER DIRECTLY OR INDIRECTLY, FROM ANY OF OUR RECOMMENDED WORKOUT PLANS OR EXERCISES. YOU ASSUME SOLE RESPONSIBILITY FOR PERFORMING THE WORKOUTS AND EXERCISES THAT THE SERVICE PROVIDE WITH PROPER FORM, AS RISK OF INJURY OR ILLNESS INCREASES WITH IMPROPER FORM.</p>
					</>
				),
			},
			{
				id: "credits-subscription-terms",
				title: "Credits & Subscription Terms",
				content: (
					<>
						<ul>
							<li>All users with an active subscription will receive <strong>6 analysis credits per month</strong>, automatically added on the <strong>first day of each calendar month</strong>.</li>
							<li>You may <strong>purchase additional credits</strong> at any time. Purchased credits will be <strong>used first</strong> before any monthly subscription credits.</li>
							<li><strong>Credits can only be used while you have an active subscription</strong>. If your subscription ends, your credits will be paused until you re-subscribe.</li>
							<li><strong>We reserve the right to change, limit, or remove the number of monthly credits granted at any time</strong>, with or without prior notice.</li>
						</ul>
					</>
				),
			},
			{
				id: "ai-powered-analysis-disclaimer",
				title: "AI-Powered Analysis & Disclaimer",
				content: (
					<>
						<ul>
							<li>Our match analysis is powered by <strong>Google’s Gemini AI</strong>. By using the analysis feature, you agree to <strong>share CoreMotion and WorkoutData</strong> (e.g., heart rate, acceleration, movement patterns) with Google Gemini for the purpose of generating performance insights.</li>
							<li>By using this feature, you also agree to <strong>Google’s Terms of Service</strong>.</li>
							<li>The analysis provided is <strong>algorithmic, estimated, and not guaranteed to be accurate</strong>. It is not intended to replace advice from a coach, trainer, or healthcare professional.</li>
							<li><strong>We are not responsible</strong> for any training decisions, injuries, or negative outcomes resulting from reliance on AI-generated feedback.</li>
						</ul>
					</>
				),
			},
			{
				id: "corrections",
				title: "Corrections",
				content: (
					<>
						<p>There may be information on the Site that contains typographical errors, inaccuracies, or emissions, including descriptions, pricing, availability, and various other information. We reserve the right to correct any errors, inaccuracies, or omissions and to change or update the information on the Site at any time, without prior notice.</p>
					</>
				),
			},
			{
				id: "subscriptions",
				title: "Subscriptions",
				content: (
					<>
						<p>The service provide different subscriptions plans with auto-renew at the end of each month, week or year (depends of the plan), unless cancelled 24-hours in advance. Payment is charged to your Apple Account at confirmation of purchase. You may manage your subscription and turn off auto-renewal by going to your phone’s Settings app. No cancellation of the current subscription is allowed during active period. By upgrading, you accept the present Terms of Use &amp; Privacy Policy.</p>
					</>
				),
			},
			{
				id: "user-responsibilities",
				title: "User Responsibilities",
				content: (
					<>
						<ul>
							<li>You are responsible for maintaining the confidentiality of your account credentials.</li>
							<li>You are responsible for all activities that occur under your account.</li>
						</ul>
					</>
				),
			},
			{
				id: "modifications-and-interruptions",
				title: "Modifications and Interruptions",
				content: (
					<>
						<p>We reserve the right to change, modify, or remove the contents of the Site at any time or for any reason at our sole discretion without notice. However, we have no obligation to update any information on our Site. We also reserve the right to modify or discontinue all or part of the Site without notice at any time. We will not be liable to you or any third party for any modification, price change, suspension, or discontinuance of the Site.</p>
						<p>We cannot guarantee the Site will be available at all times. We may experience hardware, software, or other problems or need to perform maintenance related to the Site, resulting in interruptions, delays, or errors. We reserve the right to change, revise, update, suspend, discontinue, or otherwise modify the site at any time or for any reason without notice you. You agree that we have no liability whatsoever for any loss, damage, or inconvenience caused by your inability to access or use the Site during any downtime or discontinuance of the Site. Nothing in these Terms of Use will be construed to obligate us to maintain and support the Site or to supply any corrections, updates, or releases in connection therewith.</p>
					</>
				),
			},
			{
				id: "license",
				title: "License",
				content: (
					<>
						<p>We may terminate or suspend access to our Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity and limitations of liability.</p>
					</>
				),
			},
			{
				id: "limitation-of-liability",
				title: "Limitation of Liability",
				content: (
					<>
						<p>To the fullest extent permitted by law, Waraira Tek LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the application or your data stored in our servers.</p>
						<p>To the maximum extent permitted by law, <strong>Waraira Tek LLC</strong> and <strong>Racketly</strong> shall not be liable for any damages arising from:</p>
						<ul>
							<li>Unauthorized access to user data.</li>
							<li>Service disruptions, loss of data, or technical failures.</li>
							<li>Any reliance on content or recommendations within the Application.</li>
						</ul>
					</>
				),
			},
			{
				id: "changes-to-terms",
				title: "Changes to Terms",
				content: (
					<>
						<p>We may update these Terms &amp; Conditions at any time. Continued use of the Application after updates constitutes acceptance of the revised Terms.</p>
						<p>For inquiries, contact us at: <strong><a href="mailto:support@warairatek.com">support@warairatek.com</a></strong>.</p>
					</>
				),
			},
		],
	},
};
